import { Button, Popover, Stack, Typography } from '@mui/material';
import { type Condition } from '@repo/api-gw-sdk';
import {
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
  type UIEventHandler,
} from 'react';

import { SelectionPanel } from '@/components/panels/selectionPanel/selectionPanel';
import { ScrollableContent } from '@/components/scroll/scrollableContent';
import { ScrollableWrapper } from '@/components/scroll/scrollableContext';
import { ScrollableHeader } from '@/components/scroll/scrollableHeader';
import { useScrollable } from '@/components/scroll/useScrollable';
import {
  isValueCondition,
  type FilterProperty,
  type MultiSelectConditionPanelProps,
} from '@/types/advanceFilter';
import { pluralize } from '@/utils/string';

import { AddValuesPanel } from '../panels/addValuesPanel';
import { MultiSelectFilter } from '../queryBuilder/filters';
import { SearchBar } from '../search/searchBar';
import { TextDetailsTuple } from '../text/textDetailsTuple';

export const inlineDropdownStyle = {
  boxShadow: 'none',
  '.MuiOutlinedInput-notchedOutline': { border: 0 },
  // The outlined Select variant has no hover affordance (the theme only adds
  // one for the standard variant). These borderless inline dropdowns read as
  // buttons, so give them the same hover background as menu items / text
  // buttons.
  '&:hover': {
    backgroundColor: 'var(--mui-palette-primary-dark)',
  },
};

const ValuesFilter = (props: {
  property: FilterProperty;
  entityName: string;
  isLoading: boolean;
  options: {
    id: string;
    name?: string;
    Image?: React.ReactNode;
  }[];
  condition: Condition;
  onChange: (condition: Condition) => void;
  showAddNewButton: boolean;
  showSearchBox: boolean;
  showNameFirst: boolean;
  shouldShowId: boolean;
  showSelectAll: boolean;
  useImagesInSelectedView?: boolean;
  onSearchChange?: (value: string) => void;
}) => {
  const [valueFilter, setValueFilter] = useState('');

  const selectedValues = useMemo(
    () => (isValueCondition(props.condition) && props.condition?.value) || [],
    [props.condition]
  );

  const serverSideSearch = !!props.onSearchChange;

  const handleSearchChange = (value: string) => {
    setValueFilter(value);
    props.onSearchChange?.(value);
  };

  const values = useMemo(
    () =>
      props.options
        .filter(
          (option) =>
            serverSideSearch ||
            !valueFilter ||
            option.id.toLowerCase().includes(valueFilter.toLowerCase()) ||
            option.name?.toLowerCase().includes(valueFilter.toLowerCase())
        )
        .sort(),
    [props.options, valueFilter, serverSideSearch]
  );

  const nothingToSearch =
    !props.isLoading && !props.options.length && !serverSideSearch;

  if (nothingToSearch && props.showAddNewButton) {
    return (
      <AddValuesPanel
        entityName={props.entityName}
        addedValues={selectedValues}
        onAddClick={(value) => {
          props.onChange({ ...props.condition, value });
        }}
        renderItem={(item) => (
          <SelectionItem
            item={item}
            shouldShowId={props.shouldShowId}
            showNameFirst={props.showNameFirst}
            useImagesInSelectedView={props.useImagesInSelectedView}
            options={props.options}
          />
        )}
        itemKey={(item) => item}
        clearAll={() => props.onChange({ ...props.condition, value: [] })}
        onRemove={(item) => {
          const values = selectedValues.filter((x) => x !== item);
          props.onChange({ ...props.condition, value: values });
        }}
      />
    );
  }

  return (
    <SelectionPanel
      isLoading={serverSideSearch ? false : props.isLoading}
      drawerProps={{
        items: selectedValues,
        entityType: props.entityName,
        onRemove: (item) => {
          const values = selectedValues.filter((x) => x !== item);
          props.onChange({ ...props.condition, value: values });
        },
        renderItem: (item) => (
          <SelectionItem
            item={item}
            shouldShowId={props.shouldShowId}
            showNameFirst={props.showNameFirst}
            useImagesInSelectedView={props.useImagesInSelectedView}
            options={props.options}
          />
        ),
        itemKey: (item) => item,
        clearAll: () => props.onChange({ ...props.condition, value: [] }),
      }}
    >
      <ScrollableWrapper>
        <ValuesFilterBody
          {...props}
          values={values}
          selectedValues={selectedValues}
          valueFilter={valueFilter}
          handleSearchChange={handleSearchChange}
        />
      </ScrollableWrapper>
    </SelectionPanel>
  );
};

const ValuesFilterBody = (
  props: Parameters<typeof ValuesFilter>[0] & {
    values: { id: string; name?: string; Image?: React.ReactNode }[];
    selectedValues: string[];
    valueFilter: string;
    handleSearchChange: (value: string) => void;
  }
) => {
  const { isScrolledVertically, setIsScrolledVertically } = useScrollable();

  const onScroll: UIEventHandler<HTMLDivElement> = (event) => {
    const target = event.target as HTMLDivElement;
    if (typeof target.scrollTop === 'number') {
      const isScrolled = target.scrollTop > 0;
      if (isScrolled !== isScrolledVertically) {
        setIsScrolledVertically(isScrolled);
      }
    }
  };

  return (
    <>
      <ScrollableHeader className='px-[24px] py-[12px]'>
        <Typography className='my-[12px]' variant='body1'>
          {`Select one or more ${pluralize(props.entityName)}`}
        </Typography>
        {props.showSearchBox && (
          <SearchBar
            value={props.valueFilter}
            setValue={props.handleSearchChange}
            placeholder={`Search by name or ID`}
            selectedValues={props.selectedValues}
            onAddClick={(value) => {
              props.onChange({ ...props.condition, value });
            }}
            showAddButton={props.showAddNewButton}
          />
        )}
      </ScrollableHeader>
      <ScrollableContent className='px-[24px] py-[12px]'>
        {props.values.length === 0 && (
          <Typography variant='body1' className='my-[12px]'>
            {props.showAddNewButton
              ? `No matching values found. Try a new search term, or add this value manually to include it once it’s discovered.`
              : `No matching values found. Try a new search term.`}
          </Typography>
        )}
        {props.values.length > 0 && (
          <MultiSelectFilter
            property={props.property}
            data-testid={`filter-${props.property.name}`}
            conditions={[props.condition]}
            onChange={(conditions) =>
              conditions.length
                ? props.onChange(conditions[0])
                : props.onChange({ ...props.condition, value: [] })
            }
            options={props.values}
            onVirtuosoScroll={onScroll}
            virtuosoStyles={{
              height: props.selectedValues.length ? '270px' : '330px',
              marginRight: '-24px',
              paddingRight: '24px',
            }}
            showNameFirst={props.showNameFirst}
            shouldShowId={props.shouldShowId}
            showSelectAll={props.showSelectAll}
          />
        )}
      </ScrollableContent>
    </>
  );
};

const SelectionItem = ({
  item,
  shouldShowId,
  showNameFirst,
  useImagesInSelectedView,
  options,
}: {
  item: string;
  shouldShowId: boolean;
  showNameFirst: boolean;
  useImagesInSelectedView?: boolean;
  options: MultiSelectConditionPanelProps['options'];
}) => {
  const option = options.find((x) => x.id === item);

  if (useImagesInSelectedView && option?.Image) {
    return (
      <Stack
        direction='row'
        sx={{
          overflow: 'hidden',
          alignItems: 'center',
        }}
      >
        {option.Image}
      </Stack>
    );
  }

  return (
    <Stack
      sx={{
        gap: '4px',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction='row'
        sx={{
          overflow: 'hidden',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        {option?.Image}
        <TextDetailsTuple
          primaryProp={
            shouldShowId ? (showNameFirst ? option?.name : option?.id) : item
          }
          secondaryProp={showNameFirst ? option?.id : option?.name}
        />
      </Stack>
      {!option && (
        <Typography
          sx={{
            color: 'var(--mui-palette-info-main)',
          }}
        >
          Not discovered yet
        </Typography>
      )}
    </Stack>
  );
};

export const ConditionPanelTriggerButton = forwardRef<
  HTMLButtonElement,
  {
    selectedValues: string[];
    options: MultiSelectConditionPanelProps['options'];
    onClick: () => void;
    useImagesInSelectedView?: boolean;
    shouldShowId?: boolean;
  }
>(
  (
    { selectedValues, onClick, options, useImagesInSelectedView, shouldShowId },
    ref
  ) => {
    return (
      <Button
        ref={ref}
        onClick={onClick}
        disableFocusRipple
        size='small'
        className='px-0 py-[5px] min-h-[32px] h-fit-content'
      >
        <Stack
          direction={'row-reverse'}
          className='pl-[12px] pr-[8px]'
          sx={{
            height: 'fit-content',
            alignItems: 'start',
            gap: '8px',
          }}
        >
          <i className='material-symbols-arrow-drop-down-rounded text-xl align-middle min-w-[22px] min-h-[22px] w-[22px] h-[22px]' />
          {selectedValues.length === 0 && (
            <Typography
              variant='button'
              className='text-left font-medium'
              sx={{
                color: 'var(--mui-palette-text-disabled)',
              }}
            >
              Select
            </Typography>
          )}
          {!useImagesInSelectedView && selectedValues.length > 0 && (
            <Typography
              variant='button'
              className='text-left font-medium'
              sx={{ color: 'currentcolor' }}
            >
              {selectedValues
                .map((x) => {
                  const item = options.find((y) => y.id === x);
                  return item ? (shouldShowId ? item?.id : item?.name) : x;
                })
                .join(', ')}
            </Typography>
          )}
          {useImagesInSelectedView && selectedValues.length > 0 && (
            <Stack
              direction='row'
              sx={{
                flexWrap: 'wrap',
                columnGap: '8px',
                rowGap: '4px',
                '& > *': { flexShrink: 0 },
              }}
            >
              {selectedValues.map(
                (x) => options.find((y) => y.id === x)?.Image || x
              )}
            </Stack>
          )}
        </Stack>
      </Button>
    );
  }
);

export const MultiSelectConditionPanel = (
  props: MultiSelectConditionPanelProps
) => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [popoverOpen, setPopoverOpen] = useState(props.popoverOpen || false);

  useEffect(() => {
    if (props.popoverOpen !== undefined) {
      setPopoverOpen(props.popoverOpen);
    }
  }, [props.popoverOpen]);

  const selectedValues =
    (isValueCondition(props.condition) && props.condition?.value) || [];

  return (
    <>
      {!props.hideButton && (
        <ConditionPanelTriggerButton
          ref={anchorRef}
          onClick={() => setPopoverOpen(true)}
          useImagesInSelectedView={props.useImagesInSelectedView}
          shouldShowId={props.shouldShowId}
          options={props.options}
          selectedValues={selectedValues}
        />
      )}
      <ValuesConditionPanelPopover
        showAddNewButton={props.showAddNewButton}
        showSearchBox={props.showSearchBox}
        property={props.property}
        entityName={props.entityName}
        isLoading={props.isLoading}
        options={props.options}
        condition={props.condition}
        anchorRef={props.anchorRef || anchorRef}
        popoverOpen={popoverOpen}
        selectedValues={selectedValues}
        onChange={props.onChange}
        onClose={() => {
          setPopoverOpen(false);
          props.onClose?.();
        }}
        shouldShowId={!!props.shouldShowId}
        showNameFirst={!!props.showNameFirst}
        showSelectAll={!!props.showSelectAll}
        useImagesInSelectedView={props.useImagesInSelectedView}
        onSearchChange={props.onSearchChange}
      />
    </>
  );
};

const ValuesConditionPanelPopover = (props: {
  property: FilterProperty;
  entityName: string;
  isLoading: boolean;
  options: MultiSelectConditionPanelProps['options'];
  condition: Condition;
  onChange: (condition: Condition) => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  popoverOpen: boolean;
  selectedValues: string[];
  showAddNewButton: boolean;
  showSearchBox: boolean;
  showNameFirst: boolean;
  shouldShowId: boolean;
  showSelectAll: boolean;
  useImagesInSelectedView?: boolean;
  onSearchChange?: (value: string) => void;
}) => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (props.popoverOpen) {
      setTimeout(() => setIsReady(true), 100);
    } else {
      setIsReady(false);
    }
  }, [props.popoverOpen]);

  return (
    <Popover
      open={props.popoverOpen}
      anchorEl={props.anchorRef.current}
      onClose={() => props.onClose()}
    >
      <Stack className='w-[308px]'>
        {isReady && (
          <ValuesFilter
            showAddNewButton={props.showAddNewButton}
            showSearchBox={props.showSearchBox}
            property={props.property}
            entityName={props.entityName}
            isLoading={props.isLoading}
            options={props.options}
            condition={{ ...props.condition }}
            onChange={props.onChange}
            showNameFirst={props.showNameFirst}
            shouldShowId={props.shouldShowId}
            showSelectAll={props.showSelectAll}
            useImagesInSelectedView={props.useImagesInSelectedView}
            onSearchChange={props.onSearchChange}
          />
        )}
      </Stack>
    </Popover>
  );
};
